import Konva from 'konva';
import React, { useEffect, useMemo, useRef } from "react";
import { Arrow, Ellipse, Group, Label, Layer, Rect, Shape, Tag, Text, Transformer } from 'react-konva';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { useCommentsContext } from "../../../hooks/FullscreenAnnotationModal";
import { drawCloud } from '../utils/cloudAlgorithm';
import {
  ANNOTATION_TYPE,
  PAGE_MODE_TYPE as PageModeType,
  TOOL_TYPE,
} from "../utils/types";

const AnnotationLayer = ({
  activeTool,
  newAnnotation,
  pageMode,
  pdfDimensions,
  pdfPageNumber,
  setToolSettings,
  stageRef,
  toolSettings,
  viewportTransform,
}) => {

  const {
    annotations,
    normalizeAnnotation,
    pageComments,
    removeAnnotation,
    selectedFigureId: selectedId,
    setSelectedFigureId: setSelectedId,
    updateAnnotation,
  } = useCommentsContext();

  const figureNumberings = useMemo(() => {
    const result = {};
    pageComments(pdfPageNumber)
      .flatMap(n => n.connectedFigureIds
        .map(p => ({
          id: p,
          label: `${n.pageNo}.${n.versionCommentNumber}`,
        })))
      .forEach(o => result[o.id] = o.label);
    return result;
  }, [pageComments, annotations, pdfPageNumber]);

  const shapeRef = useRef();
  const transformerRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !transformerRef.current) {
      return;
    }

    const shape = selectedId ? (stage.children[1].children.find(n => n.attrs.id === selectedId)) : undefined;
    if (shape == transformerRef.current) {
      return;
    }

    if (shape) {
      transformerRef.current.nodes([shape]);
    } else {
      transformerRef.current.nodes([]);
    }

    transformerRef.current.getLayer().batchDraw();
  }, [selectedId, stageRef]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Delete" && selectedId) {
        removeAnnotation(selectedId);
        setSelectedId(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedId, removeAnnotation, setSelectedId]);

  const getTransformedPosition = (e) => {
    const node = e.target;
    const stage = node.getStage();
    const stageTransform = stage.getAbsoluteTransform().copy();

    stageTransform.invert();

    const pos = node.absolutePosition();
    const stagePos = stageTransform.point(pos);

    return { node, stagePos };
  };

  const handleTransformEnd = (e, id) => {
    const { node, stagePos } = getTransformedPosition(e);

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    const updatedAnnotation = annotations.find(ann => ann.id === id);
    if (updatedAnnotation) {
      const newAnnotation = {
        ...updatedAnnotation,
        x: stagePos.x,
        y: stagePos.y,
        width: Math.max(5, node.width() * scaleX),
        height: Math.max(5, node.height() * scaleY),
      };
      updateAnnotation(id, newAnnotation);
    }
  };

  const handleSelect = (id) => {
    if (activeTool !== TOOL_TYPE.SELECT) {
      return;
    }

    setSelectedId(id);
    const selectedAnnotation = annotations.find(ann => ann.id === id);
    if (selectedAnnotation) {

      let newToolSettings = {
        ...toolSettings,
        tool: TOOL_TYPE.SELECT,
        lineWeight: selectedAnnotation.lineWeight,
        borderColor: selectedAnnotation.borderColor,
        fillColor: selectedAnnotation.fillColor || 'transparent'
      }

      setToolSettings(newToolSettings);
    }
  };

  const handleDragStart = (e, id) => {
    if (activeTool === TOOL_TYPE.SELECT) return true;

    //We should not be here....
  };

  const handleDragEnd = (e, id) => {
    if (activeTool !== TOOL_TYPE.SELECT) return false;

    const { node, stagePos } = getTransformedPosition(e);

    const updatedAnnotation = annotations.find(ann => ann.id === id);
    if (updatedAnnotation) {
      const newAnnotation = {
        ...updatedAnnotation,
        x: stagePos.x,
        y: stagePos.y,
      };
      updateAnnotation(id, newAnnotation);
    }
  };

  const CommentLabel = ({ text, offsetX, offsetY, selected }) => {
    if (!text) {
      return;
    }

    const fontSize = 14;

    const tagProps = {
      cornerRadius:
        fontSize / 2,
      fill:
        selected
          ? "rgb(53,104,151)"
          : "rgba(53,104,151,0.5)",
    };

    const textProps = {
      fill: "#fff",
      fontSize,
      offsetY: -1,
      padding: 2,
      text,
    };

    const textSize = new Konva.Text(textProps).measureSize(text);

    const labelWidth = textSize.width + 2 * textProps.padding;

    return (
      <Label
        offsetX={labelWidth - offsetX}
        offsetY={-offsetY}
      >
        <Tag {...tagProps} />
        <Text {...textProps} />
      </Label>
    );
  };

  const drawShape = (theShape, normalize = false) => {

    const shape = normalize ? normalizeAnnotation(theShape) : theShape;

    //If we are drawing to double-sided mode, then we need to correct for the page
    //but not on page 1!!
    let x = shape.x;
    if (pageMode === PageModeType.DOUBLE && shape.pageNo !== 1) {
      if (shape.pageNo % 2 === 1) {
        x = shape.x + (pdfDimensions.width / viewportTransform.scale);
      }
    }

    const label = figureNumberings[shape.id] /*|| "P.#"*/;

    const groupProps = {
      key: shape.id,
      id: shape.id,
      draggable: true,
      x: x,
      y: shape.y,
      page: shape.pageNo,  // ???
      onClick: () => handleSelect(shape.id),
      onTap: () => handleSelect(shape.id),
      onDragStart: (e) => handleDragStart(e, shape.id),
      onDragEnd: (e) => handleDragEnd(e, shape.id),
      onTransformEnd: (e) => handleTransformEnd(e, shape.id),
      ref: shape.id === selectedId ? shapeRef : null
    };

    const colorProps = {
      strokeWidth: shape.lineWeight,
      stroke: shape.borderColor,
      fill: shape.fillColor || toolSettings.fillColor,
    };

    const isSelected = (shape.id === selectedId);

    switch (shape.subType) {

      case ANNOTATION_TYPE.RECTANGLE:
        return (
          <Group {...groupProps}
            width={shape.width}
            height={shape.height}
          >
            <Rect
              {...colorProps}
              width={shape.width}
              height={shape.height}
            />
            <CommentLabel
              offsetX={shape.width}
              offsetY={0}
              selected={isSelected}
              text={label}
            />
          </Group>
        );

      case ANNOTATION_TYPE.ELLIPSE:
        const radiusX = Math.abs(shape.width / 2);
        const radiusY = Math.abs(shape.height / 2);
        return (
          <Group {...groupProps}
            width={shape.width}
            height={shape.height}
          >
            <Ellipse
              {...colorProps}
              radiusX={radiusX}
              radiusY={radiusY}
            />
            <CommentLabel
              offsetX={radiusX}
              offsetY={-radiusY}
              selected={isSelected}
              text={label}
            />
          </Group>
        );

      case ANNOTATION_TYPE.ARROW:
        return (
          <Group {...groupProps}
            width={shape.width}
            height={shape.height}
          >
            <Arrow
              {...colorProps}
              points={[0, 0, shape.width, shape.height]}
              hitStrokeWidth={shape.lineWeight + 30}
            />
            <CommentLabel
              offsetX={Math.max(0, shape.width)}
              offsetY={Math.min(0, shape.height)}
              selected={isSelected}
              text={label}
            />
          </Group>
        );

      case ANNOTATION_TYPE.CLOUD:
        return (
          <Group {...groupProps}
            width={shape.width}
            height={shape.height}
          >
            <Shape {...colorProps}

              width={shape.width}
              height={shape.height}

              hitFunc={(context, konvaShape) => {
                const width = konvaShape.width();
                const height = konvaShape.height();

                context.beginPath();
                context.moveTo(0, 0);
                context.rect(0, 0, width, height);
                context.closePath();

                // (!) Konva specific method, it is very important
                context.fillStrokeShape(konvaShape);
              }}

              sceneFunc={(context, konvaShape) => {
                const radius = 20;
                const width = konvaShape.width() - radius;
                const height = konvaShape.height() - radius;

                drawCloud(context, { width, height }, {
                  fill: shape.fillColor || toolSettings.fillColor,
                  outline: shape.borderColor,
                  incise: 15,
                  radius: radius,
                  overlap: 0.8333,
                  stretch: false,
                });

              }}
            />
            <CommentLabel
              offsetX={shape.width}
              offsetY={0}
              selected={isSelected}
              text={label}
            />
          </Group>
        );

      default:
        return null;
    }
  };

  return (
    <Layer listening={activeTool === TOOL_TYPE.SELECT}>
      {annotations
        .filter(annotation => {
          if (pageMode === PageModeType.SINGLE) {

            // For "single" mode, return annotations for the current page
            return annotation.pageNo === pdfPageNumber;

          }
          else if (pageMode === PageModeType.DOUBLE) {

            if (pdfPageNumber === 1) {
              // Special case for the first page in double mode
              return annotation.pageNo === 1;
            }
            else if (pdfPageNumber % 2 === 0) {
              // For even pageNumber, get annotations for this page and the next one
              return annotation.pageNo === pdfPageNumber || annotation.pageNo === pdfPageNumber + 1;
            }
            else {
              // For odd pageNumber, get annotations for this page and the previous one
              return annotation.pageNo === pdfPageNumber || annotation.pageNo === pdfPageNumber - 1;
            }

          }

          // Add a fallback return statement in case pageMode doesn't match any condition
          return false;
        })
        .map((annotation) => (
          <React.Fragment key={annotation.id}>
            {drawShape(annotation)}
          </React.Fragment>
        ))
      }
      {newAnnotation && drawShape(newAnnotation, true)}
      <Transformer ref={transformerRef} flipEnabled={false} keepRatio={false} />
    </Layer>
  );
}

export default AnnotationLayer;
